import { styled } from "goober";
import { forwardRef, useRef, useState } from "react";
import { Icon } from "./Icon";
import { PressButton } from "./PressButton";
import { fieldOutline } from "./fieldOutline";

const Element = styled("input")`
  padding: 8px 12px;
  background-color: var(--surface-primary);
  color: var(--text-primary);
  ${fieldOutline("&:focus")}
  border-radius: 8px;
  border: 0;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  height: 32px;
  min-width: 320px;

  &::placeholder {
    opacity: 0.3;
  }

  &[data-secret-set]::placeholder {
    opacity: 1;
    color: var(--text-primary);
  }

  &[data-search] {
    padding-left: 32px;
    padding-right: 32px;
  }

  &:focus {
    &::placeholder {
      opacity: 0.6;
    }
    &[data-secret-set]::placeholder {
      opacity: 0.6;
      color: var(--text-secondary);
    }
  }

  &:disabled {
    background-color: var(--alpha4);
    cursor: not-allowed;
  }
`;

const SecretWrapper = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
`;

const RevealButton = styled(PressButton)`
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

const secretMask = "•".repeat(24);

type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  onFinishChange?: (value: string) => void;
  // Mask entered characters (for API keys, passwords, and other secrets).
  secret?: boolean;
  // A value is stored server-side but can't be read back; hint it with dots so
  // the field reads as set rather than empty.
  secretIsSet?: boolean;
};

export const Input = forwardRef<HTMLInputElement, Props>(
  (
    { onFinishChange, secret, secretIsSet, placeholder, style, ...props },
    ref
  ) => {
    const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const [revealed, setRevealed] = useState(false);
    const controlled = props.value !== undefined;
    const [typedValue, setTypedValue] = useState(Boolean(props.defaultValue));
    // Reveal only makes sense while the plaintext is in the field, i.e. during
    // first entry — a stored secret never comes back to the client to reveal.
    const hasValue = controlled ? String(props.value).length > 0 : typedValue;

    const element = (
      <Element
        ref={ref}
        {...props}
        data-secret-set={secret && secretIsSet ? "" : undefined}
        placeholder={secret && secretIsSet ? secretMask : placeholder}
        style={
          secret
            ? ({
                ...style,
                WebkitTextSecurity: revealed ? "none" : "disc",
                ...(hasValue ? { paddingRight: 32 } : {}),
              } as React.CSSProperties)
            : style
        }
        onChange={(e) => {
          if (!controlled) setTypedValue(e.target.value.length > 0);
          props.onChange?.(e);
          if (onFinishChange) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(
              () => onFinishChange(e.target.value),
              2000
            );
          }
        }}
        onBlur={(e) => {
          props.onBlur?.(e);
          if (onFinishChange) {
            clearTimeout(timeoutRef.current);
            onFinishChange(e.target.value);
          }
        }}
      />
    );

    if (!secret) return element;

    return (
      <SecretWrapper style={{ width: style?.width }}>
        {element}
        {hasValue && (
          <RevealButton
            aria-pressed={revealed}
            aria-label={revealed ? "Hide secret" : "Show secret"}
            tabIndex={-1}
            // Keep focus in the field so revealing doesn't blur-save a
            // half-typed secret (which would also fire a connection test).
            onMouseDown={(e) => e.preventDefault()}
            onPress={() => setRevealed((r) => !r)}
          >
            <Icon name={revealed ? "eye-closed" : "eye"} size={16} />
          </RevealButton>
        )}
      </SecretWrapper>
    );
  }
);
